/**
 * Labor Variance — Finance & Mgmt.
 * Created 2026-05-06 per Chris Georghiou request.
 *
 * Per-job estimated vs actual labor hours, with $ variance at the job's blended
 * rate. Filters by status / PM / Salesman / Job Type. All columns sortable
 * (house rule). CSV export of the current view.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Chip, Tabs, Tab,
  TextField, FormControl, InputLabel, Select, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, IconButton, Tooltip, InputAdornment,
} from '@mui/material';
import {
  BarChart as BarChartIcon, Download as DownloadIcon,
  Refresh as RefreshIcon, Search as SearchIcon, Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getLaborVariance } from '../services/api';
import { useSortableTable } from '../utils/useSortableTable';

const fmt$ = (n) => (n === null || n === undefined || n === '') ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
const fmtHrs = (n) => (n === null || n === undefined || n === '') ? '—' : `${Number(n).toFixed(0)} h`;
const fmtPct = (n) => (n === null || n === undefined || n === '') ? '—' : `${Number(n).toFixed(1)}%`;

const varianceColor = (varHrs) => {
  const v = Number(varHrs || 0);
  if (v <= 0) return 'success.main';
  if (v < 20) return 'warning.main';
  return 'error.main';
};

const SummaryCard = ({ label, value, color = 'text.primary', sub }) => (
  <Paper elevation={2} sx={{ p: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight="bold" color={color}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

export default function LaborVariance() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // filters
  const [status, setStatus] = useState('active');
  const [pm, setPm] = useState('');
  const [salesman, setSalesman] = useState('');
  const [jobType, setJobType] = useState('');
  const [threshold, setThreshold] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { status };
      if (pm) params.pm = pm;
      if (salesman) params.salesman = salesman;
      if (jobType) params.job_type = jobType;
      if (threshold) params.threshold = threshold;
      const res = await getLaborVariance(params);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (!search) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter(r =>
      [r.name, r.client_name, r.job_number, r.salesman, r.project_manager, r.job_type]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  // Table sorts (house rule)
  const jobsTable = useSortableTable(filteredRows, { defaultKey: 'variance_hours', defaultDir: 'desc' });
  const pmTable = useSortableTable(data?.by_pm || [], { defaultKey: 'variance_hours', defaultDir: 'desc' });
  const smTable = useSortableTable(data?.by_salesman || [], { defaultKey: 'variance_hours', defaultDir: 'desc' });
  const typeTable = useSortableTable(data?.by_job_type || [], { defaultKey: 'variance_hours', defaultDir: 'desc' });

  const exportCsv = () => {
    const header = ['Job#','Project','Client','Salesman','PM','JobType','Status','Phase','Contract','EstHours','ActualHours','VarianceHours','PctVar','BlendedRate','VarianceCost'];
    const rows = jobsTable.sortedRows.map(r => {
      const varH = (Number(r.actual_hours) || 0) - (Number(r.est_hours) || 0);
      const cost = varH * (Number(r.blended_rate) || 0);
      return [
        r.job_number, r.name, r.client_name, r.salesman, r.project_manager, r.job_type,
        r.status, r.phase, r.contract_amount, r.est_hours, r.actual_hours,
        r.variance_hours, r.pct_variance, r.blended_rate, cost,
      ];
    });
    const csv = [header, ...rows].map(r => r.map(v => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `labor-variance-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BarChartIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Labor Variance</Typography>
          {data?.totals && (
            <Chip label={`${data.totals.job_count} jobs`} size="small" variant="outlined" />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!filteredRows.length}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Estimated vs actual labor hours per project. Sourced from Sage WIP (synced 3× daily). Variance $ uses each job's blended rate (actual cost ÷ actual hours).
      </Typography>

      {/* Summary */}
      {data?.totals && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard label="Jobs in view" value={data.totals.job_count} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard label="Est. Hours" value={fmtHrs(data.totals.est_hours)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard label="Actual Hours" value={fmtHrs(data.totals.actual_hours)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="Variance Hrs"
              value={fmtHrs(data.totals.variance_hours)}
              color={varianceColor(data.totals.variance_hours)}
              sub={fmtPct(data.totals.pct_variance)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="Variance $"
              value={fmt$(data.totals.variance_cost)}
              color={varianceColor(data.totals.variance_hours)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="Over / Under"
              value={`${data.totals.over_count} / ${data.totals.under_count}`}
              sub="jobs over budget / under"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value)}>
                <MenuItem value="active">Active (Open)</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Salesman" value={salesman} onChange={e => setSalesman(e.target.value)} onBlur={load} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="PM" value={pm} onChange={e => setPm(e.target.value)} onBlur={load} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Job Type" value={jobType} onChange={e => setJobType(e.target.value)} onBlur={load} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              size="small" fullWidth type="number" label="Min |variance hrs|"
              value={threshold} onChange={e => setThreshold(e.target.value)} onBlur={load}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              size="small" fullWidth label="Search" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Paper elevation={1}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            <Tab label={`Jobs (${filteredRows.length})`} />
            <Tab label={`By PM (${data.by_pm?.length || 0})`} />
            <Tab label={`By Salesman (${data.by_salesman?.length || 0})`} />
            <Tab label={`By Job Type (${data.by_job_type?.length || 0})`} />
          </Tabs>

          {tab === 0 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {jobsTable.sortableCell('job_number',     'Job#')}
                    {jobsTable.sortableCell('name',           'Project')}
                    {jobsTable.sortableCell('client_name',    'Client')}
                    {jobsTable.sortableCell('salesman',       'Salesman')}
                    {jobsTable.sortableCell('project_manager','PM')}
                    {jobsTable.sortableCell('job_type',       'Type')}
                    {jobsTable.sortableCell('status',         'Status')}
                    {jobsTable.sortableCell('contract_amount','Contract', 'right')}
                    {jobsTable.sortableCell('est_hours',      'Est Hrs', 'right')}
                    {jobsTable.sortableCell('actual_hours',   'Actual Hrs', 'right')}
                    {jobsTable.sortableCell('variance_hours', 'Var Hrs', 'right')}
                    {jobsTable.sortableCell('pct_variance',   '% Var', 'right')}
                    {jobsTable.sortableCell('blended_rate',   'Rate', 'right')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobsTable.sortedRows.map((r) => (
                    <TableRow
                      key={r.id} hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/projects/${r.id}`)}
                    >
                      <TableCell><Chip label={r.job_number || '—'} size="small" variant="outlined" /></TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</TableCell>
                      <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.client_name || '—'}</TableCell>
                      <TableCell>{r.salesman || '—'}</TableCell>
                      <TableCell>{r.project_manager || '—'}</TableCell>
                      <TableCell>{r.job_type || '—'}</TableCell>
                      <TableCell><Chip label={r.status || '—'} size="small" /></TableCell>
                      <TableCell align="right">{fmt$(r.contract_amount)}</TableCell>
                      <TableCell align="right">{fmtHrs(r.est_hours)}</TableCell>
                      <TableCell align="right">{fmtHrs(r.actual_hours)}</TableCell>
                      <TableCell align="right" sx={{ color: varianceColor(r.variance_hours), fontWeight: 700 }}>
                        {(r.variance_hours > 0 ? '+' : '') + fmtHrs(r.variance_hours)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: varianceColor(r.variance_hours) }}>
                        {fmtPct(r.pct_variance)}
                      </TableCell>
                      <TableCell align="right">{fmt$(r.blended_rate)}/h</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {[1, 2, 3].includes(tab) && (() => {
            const t = tab === 1 ? pmTable : tab === 2 ? smTable : typeTable;
            const label = tab === 1 ? 'PM' : tab === 2 ? 'Salesman' : 'Job Type';
            return (
              <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {t.sortableCell('key',           label)}
                      {t.sortableCell('jobs',          'Jobs', 'right')}
                      {t.sortableCell('est_hours',     'Est Hrs', 'right')}
                      {t.sortableCell('actual_hours',  'Actual Hrs', 'right')}
                      {t.sortableCell('variance_hours','Var Hrs', 'right')}
                      {t.sortableCell('pct_variance',  '% Var', 'right')}
                      {t.sortableCell('variance_cost', 'Var $', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {t.sortedRows.map(g => (
                      <TableRow key={g.key} hover>
                        <TableCell>{g.key}</TableCell>
                        <TableCell align="right">{g.jobs}</TableCell>
                        <TableCell align="right">{fmtHrs(g.est_hours)}</TableCell>
                        <TableCell align="right">{fmtHrs(g.actual_hours)}</TableCell>
                        <TableCell align="right" sx={{ color: varianceColor(g.variance_hours), fontWeight: 700 }}>
                          {(g.variance_hours > 0 ? '+' : '') + fmtHrs(g.variance_hours)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: varianceColor(g.variance_hours) }}>
                          {fmtPct(g.pct_variance)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: varianceColor(g.variance_hours) }}>
                          {fmt$(g.variance_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
}
